import React, { useEffect, useState, useCallback } from 'react';
import {
  canReadRecipes,
  canWriteRecipes,
  canDeleteRecipes,
} from './rbacUtils';
import './RecipesSection.css';

const MEASUREMENT_UNITS = [
  { value: 'ts', label: 'ts (tsp)' },
  { value: 'Ts', label: 'Ts (Tbsp)' },
  { value: 'cup', label: 'cup' },
  { value: 'oz', label: 'oz' },
  { value: 'lb', label: 'lb' },
  { value: 'g', label: 'g' },
  { value: 'ml', label: 'ml' },
  { value: 'each', label: 'each' },
  { value: 'pinch', label: 'pinch' },
  { value: 'to taste', label: 'to taste' },
];

const COMMON_INGREDIENTS = [
  'eggs',
  'milk',
  'salt',
  'black pepper',
  'cayenne pepper',
  'ground cumin',
  'butter',
  'olive oil',
  'flour',
  'sugar',
  'baking powder',
  'garlic',
  'onion',
  'paprika',
  'chili powder',
  'soy sauce',
  'lemon juice',
  'vanilla extract',
];

function emptyIngredient() {
  return { amount: '', unit: 'ts', name: '' };
}

function newRecipeDraft() {
  return {
    id: null,
    title: '',
    directions: '',
    ingredients: [emptyIngredient()],
  };
}

function normalizeRecipeFromApi(r) {
  const x = r && typeof r === 'object' ? r : {};
  const ingredients = Array.isArray(x.ingredients) ? x.ingredients : [];
  return {
    id: typeof x.id === 'string' ? x.id : null,
    title: typeof x.title === 'string' ? x.title : '',
    directions: typeof x.directions === 'string' ? x.directions : '',
    ingredients: ingredients.length
      ? ingredients.map((row) => ({
          amount: row?.amount != null ? String(row.amount) : '',
          unit: row?.unit ? String(row.unit) : 'ts',
          name: row?.name ? String(row.name) : '',
        }))
      : [emptyIngredient()],
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : '',
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : '',
  };
}

export default function RecipesSection({ user, functionsBaseUrl, claims, onBack }) {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editor, setEditor] = useState(() => newRecipeDraft());
  const [saving, setSaving] = useState(false);

  const readOk = canReadRecipes(claims);
  const writeOk = canWriteRecipes(claims);
  const deleteOk = canDeleteRecipes(claims);

  const fetchRecipes = useCallback(async () => {
    if (!readOk || !user) {
      setRecipes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${functionsBaseUrl}/getRecipes`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || `Could not load recipes (${response.status})`);
      }
      const rows = Array.isArray(data?.recipes) ? data.recipes.map(normalizeRecipeFromApi) : [];
      setRecipes(rows);
      setError(null);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not load recipes');
    } finally {
      setLoading(false);
    }
  }, [readOk, user, functionsBaseUrl]);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  const selectRecipe = useCallback((r) => {
    const ing = Array.isArray(r.ingredients) && r.ingredients.length ? r.ingredients : [emptyIngredient()];
    setEditor({
      id: r.id,
      title: r.title || '',
      directions: r.directions || '',
      ingredients: ing.map((x) => ({
        amount: x.amount != null ? String(x.amount) : '',
        unit: x.unit || 'ts',
        name: x.name || '',
      })),
    });
  }, []);

  const newRecipe = useCallback(() => {
    setEditor(newRecipeDraft());
  }, []);

  const updateIngredient = useCallback((index, field, value) => {
    setEditor((prev) => {
      const ingredients = prev.ingredients.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      );
      return { ...prev, ingredients };
    });
  }, []);

  const addIngredientRow = useCallback(() => {
    setEditor((prev) => ({
      ...prev,
      ingredients: [...prev.ingredients, emptyIngredient()],
    }));
  }, []);

  const removeIngredientRow = useCallback((index) => {
    setEditor((prev) => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index),
    }));
  }, []);

  const saveRecipe = useCallback(async () => {
    if (!writeOk || !user) return;
    const title = editor.title.trim();
    if (!title) {
      window.alert('Please enter a recipe title.');
      return;
    }
    setSaving(true);
    try {
      const cleanedIngredients = editor.ingredients
        .map((r) => ({
          amount: String(r.amount || '').trim(),
          unit: String(r.unit || '').trim() || 'ts',
          name: String(r.name || '').trim(),
        }))
        .filter((r) => r.name.length > 0);
      const idToken = await user.getIdToken();
      const response = await fetch(`${functionsBaseUrl}/saveRecipe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          id: editor.id || undefined,
          title,
          directions: String(editor.directions || '').trim(),
          ingredients: cleanedIngredients.length ? cleanedIngredients : [emptyIngredient()],
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || `Save failed (${response.status})`);
      }
      const saved = normalizeRecipeFromApi(data.recipe || {});
      setEditor((e) => ({ ...e, id: saved.id }));
      await fetchRecipes();
    } catch (e) {
      console.error(e);
      window.alert(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [editor, writeOk, user, functionsBaseUrl, fetchRecipes]);

  const removeRecipe = useCallback(
    async (id) => {
      if (!deleteOk || !id || !user) return;
      if (!window.confirm('Delete this recipe?')) return;
      try {
        const idToken = await user.getIdToken();
        const response = await fetch(`${functionsBaseUrl}/deleteRecipe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ id }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.error || `Delete failed (${response.status})`);
        }
        if (editor.id === id) setEditor(newRecipeDraft());
        await fetchRecipes();
      } catch (e) {
        console.error(e);
        window.alert(e.message || 'Delete failed');
      }
    },
    [deleteOk, editor.id, user, functionsBaseUrl, fetchRecipes]
  );

  if (!readOk) {
    return (
      <div className="recipes-page">
        <header className="recipes-toolbar">
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            ← Home
          </button>
        </header>
        <p className="recipes-muted">You do not have access to family recipes.</p>
      </div>
    );
  }

  return (
    <div className="recipes-page">
      <header className="recipes-toolbar">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          ← Home
        </button>
        {writeOk && (
          <button type="button" className="btn btn-primary" onClick={newRecipe}>
            New recipe
          </button>
        )}
      </header>

      {error && <p className="recipes-error">{error}</p>}

      <div className="recipes-layout">
        <aside className="recipes-list-panel">
          <h2 className="recipes-list-title">Recipes</h2>
          {loading ? (
            <p className="recipes-muted">Loading…</p>
          ) : recipes.length === 0 ? (
            <p className="recipes-muted">No recipes yet.</p>
          ) : (
            <ul className="recipes-list">
              {recipes.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    className={`recipes-list-item${editor.id === r.id ? ' is-active' : ''}`}
                    onClick={() => selectRecipe(r)}
                  >
                    {r.title || 'Untitled'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="recipes-editor">
          <label className="recipes-field">
            <span className="recipes-label">Title</span>
            <input
              type="text"
              className="recipes-input"
              value={editor.title}
              onChange={(e) => setEditor((s) => ({ ...s, title: e.target.value }))}
              disabled={!writeOk}
              placeholder="e.g. Grandma’s cornbread"
            />
          </label>

          <label className="recipes-field">
            <span className="recipes-label">Directions</span>
            <textarea
              className="recipes-textarea"
              rows={12}
              value={editor.directions}
              onChange={(e) => setEditor((s) => ({ ...s, directions: e.target.value }))}
              disabled={!writeOk}
              placeholder="Step-by-step how to prepare the recipe…"
            />
          </label>

          <div className="recipes-field">
            <div className="recipes-label-row">
              <span className="recipes-label">Ingredients</span>
              {writeOk && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={addIngredientRow}>
                  + Add row
                </button>
              )}
            </div>
            <div className="recipes-ingredients">
              {editor.ingredients.map((row, idx) => (
                <div key={idx} className="recipes-ingredient-row">
                  <input
                    type="text"
                    className="recipes-input recipes-input--amount"
                    placeholder="Amt"
                    value={row.amount}
                    onChange={(e) => updateIngredient(idx, 'amount', e.target.value)}
                    disabled={!writeOk}
                  />
                  <select
                    className="recipes-select"
                    value={row.unit}
                    onChange={(e) => updateIngredient(idx, 'unit', e.target.value)}
                    disabled={!writeOk}
                  >
                    {MEASUREMENT_UNITS.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className="recipes-input recipes-input--grow"
                    placeholder="Ingredient"
                    list="common-ingredients-list"
                    value={row.name}
                    onChange={(e) => updateIngredient(idx, 'name', e.target.value)}
                    disabled={!writeOk}
                  />
                  {writeOk && editor.ingredients.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-icon recipes-remove"
                      onClick={() => removeIngredientRow(idx)}
                      aria-label="Remove ingredient row"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            <datalist id="common-ingredients-list">
              {COMMON_INGREDIENTS.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </div>

          {writeOk && (
            <div className="recipes-actions">
              <button type="button" className="btn btn-primary" onClick={saveRecipe} disabled={saving}>
                {saving ? 'Saving…' : 'Save recipe'}
              </button>
              {deleteOk && editor.id && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => removeRecipe(editor.id)}
                >
                  Delete recipe
                </button>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
